/**
 * Product categories and subcategories structure
 */

export interface SubCategory {
  label: string;
  value: string;
  children?: SubCategory[];
}

export interface Category {
  label: string;
  value: string;
  subcategories: SubCategory[];
}

/**
 * Structured product categories with subcategories
 */
export const PRODUCT_CATEGORIES: Category[] = [
  {
    label: 'Select Category',
    value: '',
    subcategories: []
  },
  {
    label: 'Fashion',
    value: 'Fashion',
    subcategories: [
      {
        label: 'Men',
        value: 'Men',
      },
      { label: 'Clothing', value: 'Clothing' },
      { label: 'Shoes', value: 'Shoes' },
      { label: 'Accessories', value: 'Accessories' },
      {
        label: 'Women',
        value: 'Women',
      }
    ]
  },
  {
    label: 'Home & Decor',
    value: 'Home & Decor',
    subcategories: [
      { label: 'Furniture', value: 'Furniture' },
      { label: 'Decoration', value: 'Decoration' },
      { label: 'Kitchen Equipment', value: 'Kitchen Equipment' }
    ]
  },
  {
    label: 'Beauty & Wellness',
    value: 'Beauty & Wellness',
    subcategories: [
      { label: 'Cosmetics', value: 'Cosmetics' },
      { label: 'Perfumes', value: 'Perfumes' },
      { label: 'Skincare & Body Care', value: 'Skincare & Body Care' }
    ]
  },
  {
    label: 'Technology',
    value: 'Technology',
    subcategories: [
      { label: 'Smartphones', value: 'Smartphones' },
      { label: 'Tech Accessories', value: 'Tech Accessories' },
      { label: 'Smart Devices', value: 'Smart Devices' }
    ]
  },
  {
    label: 'Kids & Toys',
    value: 'Kids & Toys',
    subcategories: [
      { label: 'Educational Games', value: 'Educational Games' },
      { label: 'Outdoor Toys', value: 'Outdoor Toys' },
      { label: "Kids' Clothing", value: "Kids' Clothing" }
    ]
  },
  {
    label: 'Sports & Fitness',
    value: 'Sports & Fitness',
    subcategories: [
      { label: 'Sportswear', value: 'Sportswear' },
      { label: 'Sports Accessories', value: 'Sports Accessories' },
      { label: 'Fitness Equipment', value: 'Fitness Equipment' }
    ]
  },
  {
    label: 'Jewelry & Watches',
    value: 'Jewelry & Watches',
    subcategories: [
      { label: 'Fashion Jewelry', value: 'Fashion Jewelry' },
      { label: 'Fine Jewelry', value: 'Fine Jewelry' },
      { label: 'Watches', value: 'Watches' },
      { label: 'Accessories', value: 'Accessories' }
    ]
  }
];

/**
 * Get all subcategories for a given category ID
 * @param categoryId - The ID of the category to get subcategories for
 * @returns An array of subcategories
 */
export const getSubcategoriesForCategory = (categoryId: string): SubCategory[] => {
  const category = PRODUCT_CATEGORIES.find(cat => cat.value === categoryId);
  return category ? category.subcategories : [];
};

/**
 * Flatten nested subcategories into a single array
 * @param subcategories - Array of subcategories, possibly with nested children
 * @returns Flattened array of all subcategories
 */
export const getAllSubcategories = (subcategories: SubCategory[]): SubCategory[] => {
  return subcategories.reduce((acc, subcategory) => {
    acc.push(subcategory);
    if (subcategory.children && subcategory.children.length > 0) {
      acc.push(...subcategory.children);
    }
    return acc;
  }, [] as SubCategory[]);
};

/**
 * Get all subcategories for a given category as a flat array
 * @param categoryId - The ID of the category to get subcategories for
 * @returns A flattened array of all subcategories
 */
export const getFlatSubcategoriesForCategory = (categoryId: string): SubCategory[] => {
  const subcategories = getSubcategoriesForCategory(categoryId);
  return getAllSubcategories(subcategories);
}; 